import React, { useState, useEffect } from 'react';
import staffApi from '../staffApi';
import CreateTestModal from './components/CreateTestModal';
import SendLinkModal from './components/SendLinkModal';
import ManageLinksModal from './components/ManageLinksModal';
import SubmissionsModal from './components/SubmissionsModal';
import ViewQuestionsModal from './components/ViewQuestionsModal';

export default function TutorTests() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSendLinkModal, setShowSendLinkModal] = useState(false);
  const [showManageLinksModal, setShowManageLinksModal] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [showViewQuestionsModal, setShowViewQuestionsModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);

  useEffect(() => {
    loadTests();
  }, []);

  const loadTests = async () => {
    try {
      setLoading(true);
      const data = await staffApi.getTutorTests();
      setTests(data);
    } catch (error) {
      console.error('Error loading tests:', error);
      alert('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = async (testData) => {
    try {
      await staffApi.createTutorTest(testData);
      await loadTests();
      setShowCreateModal(false);
      alert('Test created successfully!');
    } catch (error) {
      console.error('Error creating test:', error);
      alert('Failed to create test');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) {
      return;
    }
    try {
      await staffApi.deleteTutorTest(testId);
      await loadTests();
      alert('Test deleted successfully!');
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Failed to delete test');
    }
  };

  const handleSendLink = (test) => {
    setSelectedTest(test);
    setShowSendLinkModal(true);
  };

  const handleManageLinks = (test) => {
    setSelectedTest(test);
    setShowManageLinksModal(true);
  };

  const handleViewSubmissions = (test) => {
    setSelectedTest(test);
    setShowSubmissionsModal(true);
  };

  const handleViewQuestions = (test) => {
    setSelectedTest(test);
    setShowViewQuestionsModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading tests...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tests</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
        >
          + Create New Test
        </button>
      </div>

      {tests.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500 mb-4">No tests created yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
          >
            Create Your First Test
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {tests.map((test) => (
            <div key={test.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">{test.title}</h2>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Duration: {test.duration_minutes} minutes</p>
                    <p>Questions: {test.total_questions}</p>
                    <p>Created: {new Date(test.created_at).toLocaleDateString()}</p>
                    <p>Status: {test.is_active ? 'Active' : 'Inactive'}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewQuestions(test)}
                    className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                  >
                    View Questions
                  </button>
                  <button
                    onClick={() => handleViewSubmissions(test)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                  >
                    View Submissions
                  </button>
                  <button
                    onClick={() => handleManageLinks(test)}
                    className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                  >
                    Manage Links
                  </button>
                  <button
                    onClick={() => handleDeleteTest(test.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateTestModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTest}
        />
      )}

      {showSendLinkModal && selectedTest && (
        <SendLinkModal
          test={selectedTest}
          onClose={() => {
            setShowSendLinkModal(false);
            setSelectedTest(null);
          }}
        />
      )}

      {showManageLinksModal && selectedTest && (
        <ManageLinksModal
          test={selectedTest}
          onClose={() => {
            setShowManageLinksModal(false);
            setSelectedTest(null);
          }}
        />
      )}

      {showSubmissionsModal && selectedTest && (
        <SubmissionsModal
          test={selectedTest}
          onClose={() => {
            setShowSubmissionsModal(false);
            setSelectedTest(null);
          }}
        />
      )}

      {showViewQuestionsModal && selectedTest && (
        <ViewQuestionsModal
          test={selectedTest}
          onClose={() => {
            setShowViewQuestionsModal(false);
            setSelectedTest(null);
          }}
        />
      )}
    </div>
  );
}

